import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    Crosshair,
    Globe2,
    Handshake,
    ShieldCheck,
    Landmark,
    Package,
    Stethoscope,
    Flame,
    Network,
    Briefcase,
    Users,
    FileSignature,
    Link2,
    Instagram,
} from "lucide-react";

import GlobeLines from "@/components/visual/GlobeLines";
import { SectionHeading, FadeUp, Overline } from "@/components/visual/SectionHeading";
import SEO from "@/components/seo/SEO";

const positioning = [
    { title: "Precision", text: "Structured, data-driven transaction design.", icon: Crosshair },
    { title: "Power", text: "Access to global networks and institutions.", icon: Globe2 },
    { title: "Partnerships", text: "Long-term strategic engagement and trust.", icon: Handshake },
];

const capabilities = [
    {
        title: "Strategic Advisory & Consultancy",
        desc: "Business and transaction strategy, market entry, and cross-sector planning.",
        icon: Briefcase,
        to: "/services#strategic-advisory",
    },
    {
        title: "Defence & Security Advisory",
        desc: "Procurement advisory, OEM representation, and compliance alignment.",
        icon: ShieldCheck,
        to: "/services#defence-security",
    },
    {
        title: "International Trade & Commodities",
        desc: "Advisory for global commodity transactions and trade structuring.",
        icon: Package,
        to: "/services#international-trade",
    },
    {
        title: "Pharmaceuticals & Healthcare",
        desc: "Public health, healthcare infrastructure, and institutional advisory.",
        icon: Stethoscope,
        to: "/healthcare",
    },
    {
        title: "Oil & Gas & Energy",
        desc: "Energy transaction structuring, supply chains, and market access.",
        icon: Flame,
        to: "/services#energy",
    },
    {
        title: "Cross-Border Transactions",
        desc: "Multi-jurisdiction deal structuring and financial instrument advisory.",
        icon: Network,
        to: "/services#cross-border",
    },
];

const executionCapabilities = [
    { icon: Users, title: "Representation & Market Access", desc: "On-ground representation and market entry across target jurisdictions." },
    { icon: Link2, title: "Deal Structuring & Support", desc: "Commercial architecture and transaction support across parties and regulators." },
    { icon: FileSignature, title: "Documentation & Negotiation", desc: "Advisory through term sheets, agreements, and cross-party negotiation." },
    { icon: Handshake, title: "Strategic Partnerships", desc: "Institutional introductions and long-term partnership development." },
];

const regions = ["India", "Middle East", "Southeast Asia", "Europe", "Africa"];

const Home = () => {
    return (
        <div className="page-enter">
            <SEO
                title="Defence, Trade & Energy Advisory"
                description="Indravax Strategic Advisory LLP provides strategic advisory across defence, global trade, oil & gas, healthcare, and cross-border transactions."
                path="/"
                keywords="strategic advisory firm, defence advisory services, global trade advisory, commodities trading advisory, oil and gas advisory, healthcare advisory, cross-border transaction structuring, trade finance advisory"
            />
            {/* HERO */}
            <section className="relative min-h-[90vh] flex items-center overflow-hidden" data-testid="section-hero">
                <GlobeLines className="opacity-25" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#040B1A]" />
                <div className="grain-overlay" />

                <div className="hidden xl:block absolute left-6 top-1/2 -translate-y-1/2 vertical-text text-[#C9A14A]/60">
                    Indravax · Est. Delhi · Global
                </div>

                <div className="max-w-7xl mx-auto px-6 md:px-10 py-32 relative z-10 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                        className="max-w-5xl"
                    >
                        <div className="inline-flex items-center gap-3 mb-10">
                            <span className="h-px w-10 bg-[#C9A14A]" />
                            <span className="overline">Strategic Advisory · Multi-Sector · Global</span>
                        </div>

                        <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.25rem] leading-[0.98] text-white tracking-tight">
                            Strategic Advisory
                            <br />& <span className="text-[#C9A14A] italic font-light">Cross-Border</span>
                            <br />
                            Transaction Execution.
                        </h1>

                        <div className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm md:text-base text-slate-300 font-light">
                            <span>Defence.</span>
                            <span className="dot-sep" />
                            <span>Trade.</span>
                            <span className="dot-sep" />
                            <span>Energy.</span>
                            <span className="dot-sep" />
                            <span>Healthcare.</span>
                        </div>
                        <p className="mt-3 text-slate-400 max-w-xl font-light leading-relaxed">
                            Structured advisory across global markets. Precision. Power. Partnerships.
                        </p>

                        <div className="mt-14 flex flex-col sm:flex-row gap-5">
                            <Link to="/contact" className="btn-primary group" data-testid="hero-cta-consultation">
                                Request Consultation
                                <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                            </Link>
                            <Link to="/services" className="btn-secondary" data-testid="hero-cta-services">
                                View Services
                            </Link>
                        </div>
                    </motion.div>
                </div>

                <div className="absolute bottom-0 left-0 right-0 border-t border-[#C9A14A]/15 bg-[#040B1A]/60 backdrop-blur-sm">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex flex-wrap justify-between gap-4 text-[0.65rem] tracking-[0.3em] uppercase text-slate-400">
                        <span>Delhi · Dubai · Singapore · London · Lagos</span>
                        <span className="hidden md:inline">Independent · Advisory · Cross-Border</span>
                    </div>
                </div>
            </section>

            {/* POSITIONING */}
            <section className="py-28 md:py-36 relative" data-testid="section-positioning">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 border border-[#C9A14A]/20">
                        {positioning.map((p, i) => {
                            const Icon = p.icon;
                            return (
                                <FadeUp
                                    key={p.title}
                                    delay={i * 0.1}
                                    className={`p-10 md:p-14 ${i !== positioning.length - 1 ? "md:border-r border-[#C9A14A]/20" : ""} ${i !== 0 ? "border-t md:border-t-0 border-[#C9A14A]/20" : ""}`}
                                >
                                    <Icon size={28} className="text-[#C9A14A]" strokeWidth={1.2} />
                                    <h3 className="font-serif text-4xl mt-8 text-white">{p.title}</h3>
                                    <p className="mt-4 text-slate-400 font-light leading-relaxed">{p.text}</p>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* WHAT WE DO */}
            <section className="py-28 md:py-36 relative bg-[#07132E]/40" data-testid="section-what-we-do">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
                    <FadeUp className="lg:col-span-5">
                        <Overline>What We Do</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Structured advisory across <em className="text-[#C9A14A] not-italic">defence, trade, energy,</em> and <span className="italic font-light">healthcare</span> — globally.
                        </h2>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7">
                        <p className="text-lg text-slate-300 font-light leading-[1.8]">
                            Indravax Strategic Advisory LLP provides strategic advisory and transaction
                            support across defence, trade, energy, healthcare, and cross-border investments.
                            The firm specialises in <span className="text-[#C9A14A]">structuring and
                            facilitating</span> complex, multi-party engagements across jurisdictions.
                        </p>
                        <p className="mt-6 text-slate-400 font-light leading-[1.8]">
                            Indravax acts solely as an independent advisor and does not undertake regulated
                            activities without the necessary licenses or approvals.
                        </p>
                    </FadeUp>
                </div>
            </section>

            {/* CORE CAPABILITIES */}
            <section className="py-28 md:py-36" data-testid="section-capabilities">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <SectionHeading
                        overline="Core Capabilities"
                        title="Six disciplines. One advisory standard."
                        description="Each practice is delivered under a structured advisory and facilitation framework — grounded in sector expertise and cross-jurisdictional coordination."
                        testId="capabilities-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {capabilities.map((c, i) => {
                            const Icon = c.icon;
                            return (
                                <FadeUp key={c.title} delay={i * 0.06}>
                                    <div
                                        className="institutional-card h-full"
                                        data-testid={`capability-${c.title.toLowerCase().replace(/[^a-z]+/g, '-').replace(/^-|-$/g, '')}`}
                                    >
                                        <div className="flex items-start justify-between">
                                            <Icon size={30} className="text-[#C9A14A]" strokeWidth={1.1} />
                                            <span className="text-[0.65rem] tracking-[0.3em] text-slate-500 uppercase">{String(i + 1).padStart(2, "0")}</span>
                                        </div>
                                        <h3 className="font-serif text-2xl mt-8 text-white leading-tight">{c.title}</h3>
                                        <p className="mt-4 text-slate-400 font-light leading-relaxed">{c.desc}</p>
                                        <Link to={c.to} className="mt-8 inline-flex items-center gap-2 text-[0.7rem] tracking-[0.25em] uppercase text-[#C9A14A] hover:gap-4 transition-all">
                                            Explore <ArrowRight size={12} />
                                        </Link>
                                    </div>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* EXECUTION & FACILITATION */}
            <section className="py-28 md:py-36 bg-[#07132E]/40 relative overflow-hidden" data-testid="section-execution">
                <GlobeLines className="opacity-[0.08]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <SectionHeading
                        overline="Execution & Facilitation"
                        title="Advisory, translated into transaction support."
                        description="Indravax supports clients through representation, structuring, documentation, and partnership development — the mechanics that turn advisory into engagements."
                        testId="execution-heading"
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border-t border-[#C9A14A]/20">
                        {executionCapabilities.map((s, i) => {
                            const Icon = s.icon;
                            return (
                                <FadeUp
                                    key={s.title}
                                    delay={i * 0.08}
                                    className={`p-8 md:p-10 ${i !== executionCapabilities.length - 1 ? "lg:border-r border-[#C9A14A]/20" : ""} ${i !== 0 ? "border-t lg:border-t-0 border-[#C9A14A]/20" : ""} ${i === 1 ? "md:border-t-0 md:border-l md:border-[#C9A14A]/20 lg:border-l-0" : ""}`}
                                >
                                    <Icon size={28} strokeWidth={1.1} className="text-[#C9A14A]" />
                                    <h3 className="font-serif text-2xl text-white mt-6 leading-tight">{s.title}</h3>
                                    <p className="text-sm text-slate-400 mt-3 font-light leading-relaxed">{s.desc}</p>
                                </FadeUp>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* GLOBAL PRESENCE */}
            <section className="py-28 md:py-36 relative overflow-hidden" data-testid="section-global-presence">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
                    <FadeUp className="lg:col-span-5">
                        <Overline>Global Presence</Overline>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Operating across five strategic corridors.
                        </h2>
                        <p className="mt-6 text-slate-400 font-light leading-relaxed">
                            Our engagements span India, the Middle East, Southeast Asia, Europe, and Africa —
                            coordinated through institutional relationships and on-the-ground partnerships.
                        </p>
                        <ul className="mt-10 space-y-4">
                            {regions.map((r, i) => (
                                <li key={r} className="flex items-center gap-5 py-3 border-b border-[#C9A14A]/15">
                                    <span className="text-[0.7rem] tracking-[0.3em] text-[#C9A14A] w-10">0{i + 1}</span>
                                    <span className="font-serif text-2xl text-white">{r}</span>
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                    <FadeUp delay={0.2} className="lg:col-span-7 relative aspect-square max-w-[560px] mx-auto w-full">
                        <GlobeLines className="opacity-60" />
                    </FadeUp>
                </div>
            </section>

            {/* INTERNATIONAL TRADE VERTICAL */}
            <section className="py-28 md:py-36 relative overflow-hidden border-t border-[#C9A14A]/15" data-testid="section-trade">
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
                    <FadeUp className="lg:col-span-5">
                        <div className="inline-flex items-center gap-3">
                            <Package size={28} strokeWidth={1.2} className="text-[#C9A14A]" />
                            <Overline>Trade Vertical</Overline>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            International Trade & <br />
                            <span className="italic font-light text-[#C9A14A]">Commercial Facilitation.</span>
                        </h2>
                        <Link to="/trade" className="btn-secondary mt-10 inline-flex" data-testid="home-cta-trade">
                            Explore Trade Advisory <ArrowRight size={14} />
                        </Link>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7 space-y-6 text-slate-300 font-light leading-[1.9]">
                        <p className="text-lg">
                            Indravax supports international trade and cross-border commerce through structured
                            advisory, supply chain coordination, and transaction facilitation. The firm enables
                            efficient <span className="text-[#C9A14A]">import-export operations, commodities
                            trade support, and commercial alignment</span> across global markets.
                        </p>
                        <p className="font-serif text-xl text-[#C9A14A] italic pt-2">
                            Structured advisory and facilitation across global trade, supply chains, and cross-border commercial transactions.
                        </p>
                        <div className="pt-6 flex flex-wrap gap-3">
                            {["Import-Export", "Commodities", "Supply Chain", "Incoterms", "LC · SBLC · BG", "Cross-Border"].map((t) => (
                                <span key={t} className="px-3 py-1.5 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.22em] uppercase text-slate-300">
                                    {t}
                                </span>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* PUBLIC HEALTH VERTICAL */}
            <section className="py-28 md:py-36 relative overflow-hidden border-t border-[#C9A14A]/15" data-testid="section-healthcare">
                <GlobeLines className="opacity-[0.08]" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16 items-center relative">
                    <FadeUp className="lg:col-span-5">
                        <div className="inline-flex items-center gap-3">
                            <Stethoscope size={28} strokeWidth={1.2} className="text-[#C9A14A]" />
                            <Overline>Healthcare Vertical</Overline>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-white mt-6 leading-[1.05]">
                            Pharmaceuticals & <br />
                            <span className="italic font-light text-[#C9A14A]">Healthcare Advisory.</span>
                        </h2>
                        <Link to="/healthcare" className="btn-secondary mt-10 inline-flex" data-testid="home-cta-healthcare">
                            Explore Healthcare Advisory <ArrowRight size={14} />
                        </Link>
                    </FadeUp>
                    <FadeUp delay={0.15} className="lg:col-span-7 space-y-6 text-slate-300 font-light leading-[1.9]">
                        <p className="text-lg">
                            Indravax extends its advisory capabilities into public health and healthcare
                            infrastructure, supporting governments and institutions in structuring and
                            facilitating large-scale health initiatives. The firm integrates
                            <span className="text-[#C9A14A]"> policy insight, operational planning,</span> and
                            stakeholder coordination to deliver sustainable, impact-driven outcomes.
                        </p>
                        <p className="font-serif text-xl text-[#C9A14A] italic pt-2">
                            Bridging healthcare systems, infrastructure, and strategic advisory.
                        </p>
                        <div className="pt-6 flex flex-wrap gap-3">
                            {["Infrastructure", "Public Health Programs", "Supply Chain", "Policy Alignment", "Int'l Collaboration"].map((t) => (
                                <span key={t} className="px-3 py-1.5 border border-[#C9A14A]/30 text-[0.65rem] tracking-[0.22em] uppercase text-slate-300">
                                    {t}
                                </span>
                            ))}
                        </div>
                    </FadeUp>
                </div>
            </section>

            {/* INSTAGRAM STRIP */}
            <section className="py-16 md:py-20 border-t border-[#C9A14A]/15 bg-[#07132E]/40" data-testid="section-instagram">
                <div className="max-w-7xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-8">
                    <div>
                        <Overline>Follow Indravax</Overline>
                        <h3 className="font-serif text-2xl md:text-3xl text-white mt-3 leading-tight max-w-xl">
                            Stay updated on global advisory, trade insights, and strategic engagements.
                        </h3>
                    </div>
                    <a
                        href="https://www.instagram.com/indravax"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-secondary group inline-flex items-center gap-3"
                        data-testid="home-instagram-cta"
                    >
                        <Instagram size={16} strokeWidth={1.5} />
                        View Instagram
                        <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                    </a>
                </div>
            </section>

            {/* CTA */}
            <section className="py-28 md:py-32 border-t border-[#C9A14A]/15 relative" data-testid="section-home-cta">
                <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
                    <Overline>Engage with Indravax</Overline>
                    <h2 className="font-serif text-4xl md:text-6xl text-white mt-8 leading-[1.05]">
                        Structured advisory across <span className="text-[#C9A14A] italic font-light">defence, trade, energy,</span>
                        <br /> and healthcare — globally.
                    </h2>
                    <div className="mt-12 flex flex-col sm:flex-row justify-center gap-5">
                        <Link to="/contact" className="btn-primary" data-testid="home-cta-engage">
                            Request Consultation <ArrowRight size={14} />
                        </Link>
                        <Link to="/services" className="btn-secondary" data-testid="home-cta-services-bottom">
                            View Services
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
